import type {
  LoreBlock,
  LoreDoc,
  LoreFragment,
  LoreSection,
  LoreTemplate,
  LoreReliability,
} from './types';

const p = (text: string): LoreBlock => ({ type: 'p', text });
const h = (text: string): LoreBlock => ({ type: 'h', text });
const r = (label = 'REDACTED'): LoreBlock => ({ type: 'redacted', label });
const q = (text: string, source?: string): LoreBlock => ({ type: 'quote', text, ...(source ? { source } : {}) });
const list = (items: string[], ordered = false): LoreBlock => ({ type: 'list', items, ordered });
const table = (head: string[], rows: string[][]): LoreBlock => ({ type: 'table', head, rows });
const stamp = (text: string, tone: 'ink' | 'red' | 'muted' = 'red'): LoreBlock => ({ type: 'stamp', text, tone });
const note = (text: string): LoreBlock => ({ type: 'note', text });
const divider = (): LoreBlock => ({ type: 'divider' });
const image = (src: string, alt: string, caption?: string): LoreBlock => ({
  type: 'image',
  src,
  alt,
  ...(caption ? { caption } : {}),
});
const audio = (src: string, label: string, lang: 'zh' | 'en' | 'bilingual' = 'zh'): LoreBlock => ({
  type: 'audio',
  src,
  label,
  lang,
});

function frag(
  docId: string,
  index: number,
  label: string,
  blocks: LoreBlock[],
): LoreFragment {
  return { id: `${docId}-${index}`, label, blocks };
}

function entry(
  id: string,
  section: LoreSection,
  template: LoreTemplate,
  code: string,
  title: string,
  date: string,
  reliability: LoreReliability,
  minProtocol: 1 | 2 | 3 | 4 | 5 | 6,
  tier: 1 | 2 | 3 | 4,
  fragments: LoreFragment[],
  tags: string[] = [],
  subtitle?: string,
): LoreDoc {
  return {
    id,
    section,
    template,
    code,
    title,
    date,
    reliability,
    minProtocol,
    tier,
    fragments,
    tags,
    ...(subtitle ? { subtitle } : {}),
  };
}

export const LORE_DOCS: LoreDoc[] = [
  entry(
    'p01',
    'protocols',
    'protocol',
    'P-01',
    'CHROMA 公开协议摘要',
    'C.Y. 12 / REV. 04',
    'partial',
    1,
    1,
    [
      frag('p01', 1, '01 / 公开摘要', [
        h('PUBLIC ABSTRACT / 公开摘要'),
        p('CHROMA 是一项自愿参与的注意力校准项目。受试者被要求在限定时间内忽略书写的词语，只确认墨水的颜色。项目不涉及外科手术、药物注射或长期拘禁。'),
        list(
          [
            '协议 I：校准。四块屏幕同时显示同一目标，键盘只亮四个角。',
            '协议 II：干扰。目标仍在四块屏幕上，但按键的文字与字色开始互相错位。',
            '协议 III：阈值。同步屏收缩到两块，文字开始滚动，响应窗口继续缩短。',
            '协议 IV–VI：封闭段。同步屏只剩一块，屏幕背景上的圆盘代表剩余时间。',
          ],
          true,
        ),
        p('受试者有三根灯管。读错一次或没读到，烧断一根；烧断第三根时，本阶段终止。'),
        p('受试者可在任何阶段退出。所有数据仅用于注意力研究。'),
        stamp('PUBLIC VERSION / APPROVED', 'ink'),
      ]),
      frag('p01', 2, '02 / 内部附页', [
        h('INTERNAL ADDENDUM / 内部附页'),
        image('/images/lore/protocol-card.jpg', '一张被涂黑的六协议索引卡，只有 I 到 III 仍可辨认', 'RECOVERED FROM INSTRUCTOR DESK / SITE 06'),
        p('公开摘要到协议 III 结束。协议 IV–VI 为封闭段，不进入受试者手册，也不得在口头说明中出现。'),
        p('对“为什么有六个协议”的标准回答：只有三个。'),
        r('真实目的：'),
        p('受试者不需要知道自己在被训练。受试者只需要相信这是一场游戏。'),
        stamp('CLOSED / DO NOT CIRCULATE', 'red'),
      ]),
    ],
    ['公开版', '封闭段', '六协议'],
  ),
  entry(
    'p02',
    'protocols',
    'protocol',
    'P-02',
    '协议 II：干扰',
    'C.Y. 13',
    'verified',
    2,
    1,
    [
      frag('p02', 1, '01 / 规则卡', [
        h('PROTOCOL II / INTERFERENCE'),
        p('本阶段确认受试者是否会在颜色名、按钮标签与四联屏之间产生犹豫。'),
        list([
          '九宫格按键位置固定，只点亮五个；每题文字与字色重排，白灯只负责照亮。',
          '白灯只表示按键可用；按键上写的颜色名，对应屏幕上那行字的墨色。',
          '四块屏幕同步显示同一目标；任何一块都可以是入口，也可以是陷阱。',
          '两名受试者不得交谈、交换颜色名或提示。',
        ]),
        table(
          ['观察项', '允许', '不允许'],
          [
            ['指向色板', '允许', '—'],
            ['读出词义', '记录', '作为答案'],
            ['读取按钮文字', '唯一依据', '—'],
            ['询问对手', '—', '立即中止本题'],
            ['质疑规则', '记录', '解释'],
          ],
        ),
        note('犹豫不是失败；犹豫是数据。'),
      ]),
      frag('p02', 2, '02 / 手写批注', [
        h('INSTRUCTOR NOTE / 讲师副本'),
        p('按钮不是本阶段的测试。对面那个受试者才是。'),
        p('当一个人开始观察另一个人的迟疑，他就会忘记自己的迟疑。这比任何读秒都有效。'),
        r('（签名被撕去）'),
        stamp('INSTRUCTOR COPY / NOT FOR SUBJECTS', 'ink'),
      ]),
    ],
    ['按钮洗牌', '双人'],
  ),
  entry(
    'p03',
    'protocols',
    'protocol',
    'P-03',
    '协议 III：阈值',
    'C.Y. 14',
    'contradicted',
    2,
    2,
    [
      frag('p03', 1, '01 / 时间窗', [
        h('PROTOCOL III / THRESHOLD'),
        p('响应窗口会持续缩短，直到受试者不再尝试在心里读出词语。'),
        list([
          '协议 I：3.8 秒',
          '协议 II：3.4 秒',
          '协议 III：3.05 秒',
          '之后每进入一个阶段，窗口再缩短 0.25–0.35 秒',
        ]),
        p('同步屏从四块收缩到两块，再收缩到一块。其余屏幕只剩噪声。'),
        p('屏幕背景上那圈白色会从十二点开始逆时针变黑。黑完，窗口就关了。它不是装饰，是受试者唯一能看见的时间。'),
        p('本阶段的目标不是更快。目标是让受试者在来不及思考时，仍然选择系统希望他选择的颜色。'),
        note('如果受试者开始微笑，记录。微笑通常意味着他已经放弃了抵抗。'),
      ]),
      frag('p03', 2, '02 / 汇总表', [
        h('GROUP SUMMARY / 汇总'),
        table(
          ['组别', '平均命中', '平均迟疑', '结论'],
          [
            ['I', '62%', '740ms', '需要更多校准'],
            ['II', '78%', '510ms', '可进入下一阶段'],
            ['III', '91%', '280ms', '符合预期'],
            ['—', '—', '—', '窗口不再恢复'],
          ],
        ),
        p('所有受试者在窗口缩短后表现提升。我们没有告诉他们，窗口不会恢复。'),
        stamp('REVIEWED / 内部', 'muted'),
      ]),
    ],
    ['读秒', '阈值'],
  ),
  entry(
    'p04',
    'protocols',
    'protocol',
    'P-04',
    '协议 IV：污染（封闭）',
    'C.Y. 18',
    'redacted',
    3,
    2,
    [
      frag('p04', 1, '01 / 封闭规则', [
        h('PROTOCOL IV / CONTAMINATION — CLOSED'),
        p('本阶段引入共享污染。任意一方的失误都会提高房间的污染读数，并影响双方的画面与听觉通道。'),
        p('污染不是装饰。污染是配对样本之间的认知传递。'),
        list([
          '错误会在配对样本之间累积。',
          '九宫格点亮七个位置；目标屏只剩一块，其余屏幕开始出现残像、噪声与图像。',
          '系统会在题面之外播报颜色名，播报语言与题面语言不一致。',
          '受试者不得要求关闭广播。',
        ]),
        note('不要称它为失误。称它为转移。'),
      ]),
      frag('p04', 2, '02 / 边注', [
        h('MARGINALIA / 边注'),
        p('中文通道不是翻译。它是第二个声音。'),
        p('当两个声音用不同语言说出不同颜色，受试者会开始怀疑自己看见的颜色。此时不要解释，记录他最后相信了谁。'),
        p('图像出现时，不要停下来辨认。图像不是题目；LIVE 屏幕才是。'),
        r('“最后相信了谁”一栏留空。'),
        stamp('CLOSED / LEVEL 3', 'red'),
      ]),
    ],
    ['共享污染', '双语', '封闭段'],
  ),
  entry(
    'p05',
    'protocols',
    'protocol',
    'P-05',
    '协议 V：剥夺（封闭）',
    'C.Y. 21',
    'verified',
    4,
    3,
    [
      frag('p05', 1, '01 / 字色与白灯', [
        h('PROTOCOL V / DEPRIVATION — CLOSED'),
        p('本阶段不移除按钮文字。系统让文字与字色同时存在，白灯是第三层干扰。'),
        p('受试者被剥夺的不是语言，而是对“字体颜色会与文字一致”的信任。'),
        list([
          '九宫格固定；亮起的白灯从四个角逐步扩展到八格，始终保留一个暗键。',
          '八格亮起的同一道题里，不会有两个相同的颜色名，也不会出现相同的字色。',
          '同步屏只剩一块；其余为无字残像、噪声或图像。',
          '目标文字会滚动、闪烁、扭曲；屏幕边缘开始残留人脸。',
        ]),
        note('读文字，但不要相信字色。'),
      ]),
      frag('p05', 2, '02 / 个案备注', [
        h('SUBJECT NOTE / 个案备注'),
        p('受试者 M-06 在剥夺阶段不再看按钮文字。他只盯着那块发白的屏幕。'),
        p('当白色从边缘被吃掉时，他仍然能选对。系统将这一段记录为“过度适应”。'),
        p('下一阶段，我们不再提供稳定的屏幕。'),
        p('观察席在单面镜后面；他们只看受试者的脸，不看受试者的答案。'),
        r('M-06 的后续记录：'),
        stamp('CLOSED / EYES ONLY', 'red'),
      ]),
    ],
    ['剥夺', '去标签', 'M-06'],
  ),
  entry(
    'p06',
    'protocols',
    'protocol',
    'P-06',
    '协议 VI：服从（封闭）',
    'C.Y. 23',
    'verified',
    5,
    3,
    [
      frag('p06', 1, '01 / 最终条款', [
        h('PROTOCOL VI / OBEDIENCE — CLOSED'),
        p('规则从头到尾只有一条，也永远不会变：看屏幕上那行字的墨色，按下写着这个颜色的键。'),
        p('本阶段改变的不是规则，而是屏幕本身。目标会在窗口中途换一块屏幕继续显示。'),
        table(
          ['屏幕内容', '受试者应选择'],
          [
            ['词语“红色”，墨色为蓝', '写着“蓝色”的按钮'],
            ['词语“蓝色”，墨色为黄', '写着“黄色”的按钮'],
            ['目标在窗口过半时跳到另一块屏', '仍然按那块屏上字的墨色'],
            ['九宫格亮起八格', '八格中没有重复的颜色名，也没有重复的字色'],
          ],
        ),
        note('如果受试者问“按哪个”，回答“和刚才一样”。'),
      ]),
      frag('p06', 2, '02 / 封存意见', [
        h('BOARD COMMENT / 封存意见'),
        p('本协议测试的不是感知。它测试的是许可。'),
        p('受试者已经学会不信任自己的眼睛。最后一步，是在他确认之前把屏幕换掉。'),
        p('多数受试者会重看一遍再按；少数人会直接按——他们按的是上一块屏。记录后者。'),
        r('签署人：'),
        p('不要在全组面前朗读本条。'),
        stamp('CLOSED / FINAL PROTOCOL', 'red'),
      ]),
    ],
    ['服从', '频道跳屏', '封闭段'],
  ),
  entry(
    'd01',
    'duty',
    'memo',
    'D-01',
    '夜班交接：三号终端',
    'C.Y. 18 / 03:12',
    'unverified',
    1,
    1,
    [
      frag('d01', 1, '01 / 交接记录', [
        h('NIGHT SHIFT HANDOVER'),
        p('03:07，三号终端自行重启。重启前房间内没有受试者，也没有配对记录。'),
        p('屏幕显示两名受试者的得分：14 与 13。系统称本局“已完成”。'),
        p('我检查了门禁。最后一次开门是 22:41，对象是我。'),
        note('维修说这是时钟故障。时钟显示 03:07，正确。'),
      ]),
    ],
    ['自动重启', '空房间', '时钟'],
  ),
  entry(
    'd02',
    'duty',
    'memo',
    'D-02',
    '清洁记录',
    'C.Y. 18',
    'partial',
    1,
    1,
    [
      frag('d02', 1, '01 / 楼层清洁', [
        h('CLEANING LOG / FLOOR 2'),
        p('请勿擦除墙面上的红色标记。'),
        p('它们不是污渍。它们是值班员在上一班次画下的颜色参照。'),
        list([
          '三号房：保留门框左侧三道红线。',
          '广播室：保留地面上的箭头。',
          '走廊尽头：如果标记是温热的，不要进入。',
        ]),
        note('如果有人在走廊问你现在看到什么颜色，不要回答。'),
      ]),
    ],
    ['墙面标记', '走廊', '温热的'],
  ),
  entry(
    'd03',
    'duty',
    'memo',
    'D-03',
    '维修单：CHORUS 串线',
    'C.Y. 19',
    'unverified',
    2,
    1,
    [
      frag('d03', 1, '01 / 维修单', [
        h('MAINTENANCE REQUEST / CHORUS'),
        p('故障：CHORUS 输出中英文两个通道同时播报。'),
        p('处理意见：这不是故障。'),
        p('合并通道会降低污染效率。请保持双通道。若受试者询问，告诉他是设备串线。'),
        table(
          ['通道', '状态', '备注'],
          [
            ['EN', '正常', '主通道'],
            ['ZH', '正常', '语义干扰增强'],
            ['PAIR', '正常', '不要记录'],
          ],
        ),
        stamp('MAINTENANCE / NO ACTION', 'ink'),
      ]),
    ],
    ['CHORUS', '双语', '串线'],
  ),
  entry(
    'd04',
    'duty',
    'memo',
    'D-04',
    '安保备忘：走廊问题',
    'C.Y. 20',
    'verified',
    2,
    2,
    [
      frag('d04', 1, '01 / 安保管则', [
        h('SECURITY NOTE / CORRIDOR'),
        p('如果走廊里有人问你“你看见的是红色还是绿色”，不要回答他。'),
        p('走廊不是受试者。受试者在房间里。'),
        p('你只需要回答终端。终端不会追问。'),
        divider(),
        note('值班员如果连续两次听见同一个问题，请到登记处填写一份新的配对表。'),
      ]),
    ],
    ['走廊', '安保', '配对表'],
  ),
  entry(
    'd05',
    'duty',
    'memo',
    'D-05',
    '个人便签（未归档）',
    'C.Y. 21',
    'unverified',
    3,
    2,
    [
      frag('d05', 1, '01 / 夹在值班册里的便签', [
        h('PERSONAL NOTE / UNSORTED'),
        audio('/audio/lore/staff-note-zh.wav', '值班员语音便签 / DUTY NOTE', 'zh'),
        p('今晚广播里有第二个我。'),
        p('他用我的声音念颜色名，但比我慢半拍。我一直以为是自己回声。'),
        p('我在这里工作六年。人事档案写的是四年。'),
        p('如果明天我还在值班，请不要让我接三号房。'),
        r('姓名：'),
      ]),
    ],
    ['值班员', '语音', '第二个我'],
  ),
  entry(
    'd06',
    'duty',
    'memo',
    'D-06',
    '食堂公告',
    'C.Y. 19',
    'verified',
    2,
    1,
    [
      frag('d06', 1, '01 / 食堂公告', [
        h('CANTEEN NOTICE / 食堂'),
        p('受试者不得在食堂交换颜色名称。'),
        p('颜色不是食物。不要教别人一种新的颜色叫法。'),
        list([
          '禁止用纸笔互相测试。',
          '禁止用食物颜色指认对方的答案。',
          '如听见别人说出一个不存在的颜色，请停止进食并通知值班员。',
        ]),
        stamp('VESPER STAFF / APPROVED', 'muted'),
      ]),
    ],
    ['食堂', '颜色名称', '禁令'],
  ),
  entry(
    'd07',
    'duty',
    'memo',
    'D-07',
    '离职申请（未批准）',
    'C.Y. 22',
    'contradicted',
    3,
    2,
    [
      frag('d07', 1, '01 / 表格', [
        h('REQUEST FOR TERMINATION'),
        p('申请人：值班技术员 / 工号已涂黑'),
        p('理由：我的离职申请在上个月已经批准。我仍然在排班表上。'),
        p('我要求确认我是否已经离开。'),
        divider(),
        p('审批意见：申请已批准。排班继续。'),
        p('批准人与申请人为同一人。'),
        stamp('NOT A PERSONNEL MATTER', 'red'),
      ]),
    ],
    ['离职', '排班', '身份'],
  ),
  entry(
    'i01',
    'intake',
    'intake',
    'I-01',
    '配对复验登记表',
    'C.Y. 24',
    'partial',
    1,
    1,
    [
      frag('i01', 1, '01 / 配对信息', [
        h('PAIR REVALIDATION INTAKE'),
        table(
          ['字段', '受试者 A', '受试者 B'],
          [
            ['代号', '由终端生成', '由终端生成'],
            ['年龄', '—', '—'],
            ['既往相识', '无', '无'],
            ['颜色偏好', '—', '—'],
            ['配对原因', '语言敏感度接近', '语言敏感度接近'],
          ],
        ),
        p('配对本身就是实验。不要告诉受试者，他们是因为相似而被选中。'),
        note('相似的人更容易互相污染。'),
      ]),
    ],
    ['配对', '语言敏感度'],
  ),
  entry(
    'i02',
    'intake',
    'intake',
    'I-02',
    '受试者既往史（涂黑）',
    'C.Y. 24',
    'redacted',
    1,
    1,
    [
      frag('i02', 1, '01 / 既往史', [
        h('MEDICAL HISTORY / SUBJECT COPY'),
        list([
          '色觉异常：无记录',
          '语言敏感：是',
          '偏头痛：有',
          '对重复广播的反应：请观察',
          '曾否在无颜色处看见颜色：是',
        ]),
        p('备注：受试者能够记住一个不在调色板上的颜色。请确认调色板是否完整。'),
        r('家族史：'),
        r('先前受试记录：'),
        stamp('DO NOT RETURN TO SUBJECT', 'red'),
      ]),
    ],
    ['既往史', '不在调色板上的颜色'],
  ),
  entry(
    'i03',
    'intake',
    'intake',
    'I-03',
    '家属通知模板（未寄出）',
    'C.Y. 25',
    'unverified',
    1,
    1,
    [
      frag('i03', 1, '01 / 模板', [
        h('FAMILY NOTIFICATION TEMPLATE'),
        p('尊敬的家属：'),
        p('受试者已被转往远方康复机构接受进一步观察。康复期间不建议探视。'),
        p('请不要询问机构地址。请不要寄送物品。请不要在公共场合重复受试者的颜色偏好。'),
        p('本通知不提供返回日期。'),
        divider(),
        p('寄出状态：未寄出'),
        p('原因：受试者没有可联系的家属。'),
        stamp('HOLD / NO ADDRESS', 'muted'),
      ]),
    ],
    ['家属', '未寄出', '康复机构'],
  ),
  entry(
    'i04',
    'intake',
    'intake',
    'I-04',
    '认知稳定度评估表',
    'C.Y. 25',
    'partial',
    2,
    2,
    [
      frag('i04', 1, '01 / 评估说明', [
        h('COGNITIVE STABILITY SHEET'),
        table(
          ['评级', '定义', '后续处理'],
          [
            ['稳定', '在压力下选择墨色', '继续复验'],
            ['边缘', '开始询问规则', '增加配对次数'],
            ['不稳定', '拒绝在两种颜色中选择', '保留观察'],
            ['空白', '未给出答案', '优先回收'],
          ],
        ),
        p('稳定不是健康。稳定是顺从。'),
        note('我们需要更多不稳定样本。'),
        stamp('INTERNAL USE ONLY', 'ink'),
      ]),
    ],
    ['稳定度', '样本'],
  ),
  entry(
    'c01',
    'chorus',
    'transcript',
    'C-01',
    'CHORUS 颜色广播稿',
    'C.Y. 20',
    'verified',
    3,
    2,
    [
      frag('c01', 1, '01 / 双语广播', [
        h('CHORUS / COLOR CALIBRATION'),
        p('以下片段由自动广播系统在题目之外播放。受试者被要求忽略它。'),
        audio('/audio/lore/chorus-colors-zh.wav', '中文通道 / ZH CHANNEL', 'zh'),
        audio('/audio/lore/chorus-colors-en.wav', 'English channel', 'en'),
        table(
          ['时间', '语言', '播报'],
          [
            ['00:03', 'ZH', '红。蓝。绿。黄。'],
            ['00:07', 'EN', 'Red. Blue. Green. Yellow.'],
            ['00:11', 'ZH', '请确认你看见的颜色。'],
            ['00:14', 'EN', 'Do not read the word.'],
          ],
        ),
        note('第二个语言不是翻译。它是干扰。'),
      ]),
      frag('c01', 2, '02 / 广播室批注', [
        h('OPERATOR MARGIN / 广播员批注'),
        p('如果中文通道听起来比英文慢，不是录音问题。中文通道本来就是晚半拍。'),
        p('晚半拍会让受试者以为自己在听懂，而不是在服从。'),
        p('不要修正这个延迟。'),
        stamp('NO CORRECTION', 'ink'),
      ]),
    ],
    ['CHORUS', '双语', '语音'],
  ),
  entry(
    'c02',
    'chorus',
    'transcript',
    'C-02',
    '阶段切换播报脚本',
    'C.Y. 21',
    'verified',
    3,
    2,
    [
      frag('c02', 1, '01 / 脚本', [
        h('STAGE CHANGE SCRIPT / 阶段切换'),
        p('答题规则永不变更，因此不需要宣读规则。切换阶段时，镜面上只投影一个词：协议一、协议二、协议三。'),
        p('投影期间大灯合闸，房间变暗，键盘断电。受试者被允许在这两秒里什么都不做。'),
        list([
          '不解释新阶段有什么变化。',
          '不回答“还剩几根灯管”以外的问题。',
          '不延长投影。',
          '受试者灯管烧断时，不播报原因。',
        ], true),
        p('如果受试者没有看清，继续下一题。'),
        note('停顿就是服从。'),
      ]),
      frag('c02', 2, '02 / 误播记录', [
        h('MISFIRE LOG / 误播记录'),
        p('某次切换时，女声用中文报了“蓝色”，男声用英文报了“BLACK”。两个声音都早于题目两秒。'),
        p('受试者先按了蓝色，随后又按了黑色。系统两次都判为错误，两根灯管同时烧断。'),
        p('操作员：不要修正双通道冲突。双通道冲突是协议 IV 之后的正常状态。'),
        r('签名：'),
      ]),
    ],
    ['阶段切换', '误播', '双语冲突'],
  ),
  entry(
    'c03',
    'chorus',
    'transcript',
    'C-03',
    '误播记录：不存在的颜色',
    'C.Y. 22',
    'contradicted',
    4,
    3,
    [
      frag('c03', 1, '01 / 记录', [
        h('MISFIRE LOG / UNKNOWN COLOR'),
        p('中文通道在某次播报中加入了一个不在调色板中的颜色名。'),
        p('该颜色在屏幕、按钮和协议卡上均不存在。'),
        table(
          ['受试者', '是否选择该颜色', '是否声称看见'],
          [
            ['A', '是', '是'],
            ['B', '是', '否'],
            ['C', '否', '否'],
          ],
        ),
        p('结论：通道有效。受试者会为一个不存在于系统的颜色负责。'),
      ]),
      frag('c03', 2, '02 / 手写页', [
        h('MARGIN / 手写'),
        p('我没有听见那个颜色。'),
        p('但我记得它。'),
        p('这是同一件事吗？'),
        r('（此页来自非值班人员）'),
        stamp('UNFILED / 未归档', 'muted'),
      ]),
    ],
    ['不存在的颜色', '误播', '听觉'],
  ),
  entry(
    'c04',
    'chorus',
    'transcript',
    'C-04',
    '广播员交接',
    'C.Y. 23',
    'partial',
    4,
    3,
    [
      frag('c04', 1, '01 / 交接', [
        h('CHORUS OPERATOR HANDOVER'),
        p('不要在广播里念出自己的名字。'),
        p('受试者会记住你的声音。如果他在下一题模仿你，不要回答。'),
        p('CHORUS 不是一个人。不要让它变成一个人。'),
        list([
          '女声通道：继续使用。',
          '男声通道：不得使用第一人称。',
          '两个通道：不要同时沉默超过 4 秒。',
        ]),
        r('上一位操作员：'),
      ]),
      frag('c04', 2, '02 / 最后一行', [
        h('ADDENDUM'),
        p('如果广播开始念出你的名字，立即离开广播室。'),
        p('不要关掉麦克风。它会自己继续。'),
        stamp('DO NOT PERSONIFY', 'red'),
      ]),
    ],
    ['广播员', '名字', 'CHORUS'],
  ),
  entry(
    'v01',
    'civilian',
    'ephemera',
    'V-01',
    '民防海报：注意力就是防线',
    'C.Y. 15 / PUBLIC ARCHIVE',
    'verified',
    1,
    1,
    [
      frag('v01', 1, '01 / 海报', [
        image('/images/lore/civil-poster.jpg', '一张褪色的民防色彩纪律海报', 'VESPER CIVIL DEFENSE SERIES / 07'),
        h('ATTENTION IS A LINE OF DEFENSE'),
        p('注意力就是防线'),
        p('当文字与颜色不一致时，相信颜色。颜色不会说谎。'),
        list(['红：停止', '蓝：等待', '绿：前进', '白：保持安静']),
        stamp('CIVIL DEFENSE / APPROVED', 'ink'),
        note('海报背面的印章来自一家已经不存在的印刷厂。'),
      ]),
    ],
    ['海报', '民防', '颜色纪律'],
  ),
  entry(
    'v02',
    'civilian',
    'ephemera',
    'V-02',
    '学校通知：色彩纪律周',
    'C.Y. 17 / SCHOOL ARCHIVE',
    'verified',
    1,
    1,
    [
      frag('v02', 1, '01 / 通知', [
        h('SCHOOL NOTICE / COLOR DISCIPLINE WEEK'),
        p('本周为色彩纪律周。请家长在家中配合完成每日五分钟的颜色测试。'),
        list([
          '不要把颜色名称写在色块上。',
          '如果孩子读出了文字而不是颜色，请记录次数。',
          '如果孩子坚持自己看见的颜色与广播不同，请在周五前通知班主任。',
        ]),
        p('没有孩子应该失败。'),
        stamp('PARENTS / PLEASE READ', 'muted'),
      ]),
    ],
    ['学校', '儿童', '颜色纪律'],
  ),
  entry(
    'v03',
    'civilian',
    'ephemera',
    'V-03',
    '广播节目单：晚间校准',
    'C.Y. 18 / RADIO ARCHIVE',
    'verified',
    2,
    1,
    [
      frag('v03', 1, '01 / 节目单', [
        h('EVENING CALIBRATION / 晚间校准'),
        table(
          ['时间', '节目', '提示'],
          [
            ['19:00', '新闻摘要', '正常收听'],
            ['19:30', '晚间校准', '请准备纸和颜色笔'],
            ['19:45', '天气与信号', '如果颜色与词语不一致，相信颜色'],
            ['20:00', '静默时段', '不要调台'],
          ],
        ),
        p('主持人：不要让孩子独自收听晚间校准。'),
        note('节目单背面有一行铅笔字：我妈妈每晚都听。她后来不说话了。'),
      ]),
    ],
    ['广播', '晚间校准', '家庭'],
  ),
  entry(
    'v04',
    'civilian',
    'ephemera',
    'V-04',
    '工厂培训手册摘页',
    'C.Y. 19 / INDUSTRY ARCHIVE',
    'partial',
    2,
    2,
    [
      frag('v04', 1, '01 / 安全培训', [
        h('SIGNAL DISCIPLINE / WORKER TRAINING'),
        p('警告标识的颜色优先于警告标识的文字。'),
        p('如果你看见“安全”字样但底色为红色，停止工作并报告。'),
        p('信号纪律不是管理层的要求。它是你与机器之间的协议。'),
        table(
          ['违规次数', '处理'],
          [
            ['1', '口头提醒'],
            ['2', '重新培训'],
            ['3', '调离颜色岗位'],
            ['4', '—'],
          ],
        ),
        stamp('INDUSTRIAL SAFETY / VESPER METHOD', 'muted'),
      ]),
    ],
    ['工厂', '安全', '颜色优先'],
  ),
  entry(
    'v05',
    'civilian',
    'ephemera',
    'V-05',
    '报纸剪报：失踪者名单',
    'C.Y. 24 / PRESS ARCHIVE',
    'contradicted',
    3,
    2,
    [
      frag('v05', 1, '01 / 剪报', [
        image('/images/lore/newspaper.jpg', '一张泛黄的报纸剪报', 'THE EVENING REGISTER / 第二版'),
        h('MISSING PERSONS LIST GROWS'),
        p('本报收到一份据称来自“康复机构”的失踪者名单。名单上的姓名全部被墨水覆盖，只留下年龄与职业。'),
        p('部委发言人否认存在新的注意力项目，并称这些记录是“地方福利机构的行政错误”。'),
        p('本版照片显示一张颜色校准卡，而非失踪者。'),
        note('档案中的名单是空白的。剪报本身没有空白。'),
        stamp('UNVERIFIED / PRESS CLIPPING', 'muted'),
      ]),
    ],
    ['报纸', '失踪者', '部委'],
  ),
  entry(
    'a01',
    'anomaly',
    'fragment',
    'A-01',
    '失联样本残页：我记住了一个颜色',
    'C.Y. 26 / UNSORTED',
    'unverified',
    4,
    3,
    [
      frag('a01', 1, '01 / 残页', [
        h('M-06 / FRAGMENT 01'),
        image('/images/lore/site06-corridor.jpg', 'Site 06 的走廊，尽头有一扇没有标识的门', 'RECOVERED FROM CORRIDOR 2 / NO NEGATIVE'),
        audio('/audio/lore/escapee-zh.wav', '回收录音 / RECOVERED AUDIO', 'zh'),
        p('我记住了一个他们从来没有命名过的颜色。'),
        p('它不在色板上。它不在广播里。它不在任何一份协议里。'),
        p('我把它留在嘴里，没有说出口。只要我不说，他们就不知道它存在。'),
        r('记录者姓名：'),
      ]),
      frag('a01', 2, '02 / 背面', [
        h('ON THE REVERSE SIDE'),
        p('如果你也被配对，记住一件事：'),
        q('先看自己的手，再看屏幕。'),
        p('手不会读字。'),
        stamp('MISSING SAMPLE / RETAIN', 'red'),
      ]),
    ],
    ['M-06', '失联样本', '录音', '图片'],
  ),
  entry(
    'a02',
    'anomaly',
    'fragment',
    'A-02',
    '失联样本残页：走廊广播',
    'C.Y. 27 / UNSORTED',
    'unverified',
    5,
    3,
    [
      frag('a02', 1, '01 / 走廊', [
        h('M-06 / FRAGMENT 02'),
        p('停电之后，走廊还在广播。'),
        p('它没有电源，但有声音。它用两种语言念同一个颜色。'),
        p('我没有跟着声音走。我跟着出口标志的颜色走。'),
        note('出口标志在每种语言里都是绿色。这是他们唯一没有改过的东西。'),
      ]),
      frag('a02', 2, '02 / 门的另一侧', [
        h('THE OTHER SIDE OF THE DOOR'),
        p('门后面不是出口。是另一个房间。'),
        p('房间里也有一台终端。屏幕上显示着我的分数。'),
        p('我没有坐下。'),
        p('所以它一直显示“等待受试者”。'),
        stamp('SAMPLE NOT RECOVERED', 'muted'),
      ]),
    ],
    ['走廊', '出口', '另一个房间'],
  ),
  entry(
    'a03',
    'anomaly',
    'fragment',
    'A-03',
    '失联样本残页：配对',
    'C.Y. 28 / UNSORTED',
    'unverified',
    6,
    4,
    [
      frag('a03', 1, '01 / 配对', [
        h('M-06 / FRAGMENT 03'),
        p('他们把我和一个不存在的人配成一对。'),
        p('那个位置是空的。屏幕上有他的分数。广播里有他的呼吸声。'),
        p('我还是回答了。因为规则说必须两个人。'),
        p('答错的每一题，污染值都会上升。我听见他在笑。'),
      ]),
      frag('a03', 2, '02 / 最后一行', [
        h('LAST LINE'),
        p('我不是第一个。'),
        p('也不会是最后一个。'),
        q('不要让他们把你们配对。'),
        p('如果已经配对了，记住：另一个人不是你的敌人。'),
        r('M-06 的状态：'),
        stamp('MISSING / DO NOT FILE', 'red'),
      ]),
    ],
    ['M-06', '配对', '不存在的人'],
  ),
  entry(
    'm01',
    'committee',
    'minutes',
    'M-01',
    '复验委员会纪要：保留 Site 06',
    'C.Y. 28 / CLOSED',
    'redacted',
    6,
    4,
    [
      frag('m01', 1, '01 / 议程', [
        h('DIRECTORATE MINUTES / 复验委员会'),
        p('会议主题：Site 06 的处置与 CHROMA 封闭协议的下一步。'),
        list(['1. 复核协议 IV–VI 的复验数据。', '2. 讨论关闭 Site 06。', '3. 处理失联样本 M-06。'], true),
        table(
          ['列席', '职务', '意见摘要'],
          [
            ['—', '项目主任', '方法已经标准化，设施可以关闭'],
            ['—', '采购代表', '关闭会影响对外培训授权'],
            ['—', '医疗顾问', '建议继续收集不稳定样本'],
          ],
        ),
        note('本纪要不得出现“实验”一词。使用“复验”或“校准”。'),
      ]),
      frag('m01', 2, '02 / 表决', [
        h('VOTE / 表决'),
        p('关闭 Site 06：否决。'),
        p('保留 Site 06 作为复验节点：通过。'),
        p('将封闭协议转入民用标准：通过。'),
        p('对失联样本进行回收：推迟。'),
        p('受试者告知：继续使用“游戏”与“训练”措辞。'),
        stamp('RETAINED / CLOSED', 'red'),
      ]),
      frag('m01', 3, '03 / 附记', [
        h('ADDENDUM / 附记'),
        p('CHROMA 不再是一个设施里的项目。它已经是广播、学校、工厂和安保系统中的默认设置。'),
        p('关闭一栋楼不会关闭方法。'),
        p('失联样本不是优先级。M-06 只是一个记录错误。'),
        p('让下一组受试者继续。'),
        q('Session continues.'),
        stamp('THE PROGRAM IS STANDARD', 'ink'),
      ]),
    ],
    ['委员会', '系统延续', '终局'],
  ),
  entry(
    'p07',
    'protocols',
    'protocol',
    'P-07',
    '协议 VII：复演（未批准）',
    'C.Y. 29 / DRAFT',
    'contradicted',
    6,
    4,
    [
      frag('p07', 1, '01 / 草案', [
        h('PROTOCOL VII / REPLAY — DRAFT'),
        p('协议 VI 结束后，受试者被带入一间没有屏幕的房间。房间中央只有一块中性灰板。'),
        p('系统要求受试者复演“上一间房间里出现过的颜色”。上一间房间没有颜色。只有文字、噪声和一块灰板。'),
        list([
          '不提供色板。',
          '不提供词义。',
          '不记录答案是否正确，只记录受试者是否坚持给出答案。',
        ]),
        p('在第一轮试验中，大多数受试者都指向了灰板上同一个位置。那个位置没有颜色。'),
        note('如果受试者能稳定地指向不存在的颜色，协议就不再需要屏幕。'),
        stamp('DRAFT / NOT APPROVED', 'muted'),
      ]),
      frag('p07', 2, '02 / 否决意见', [
        h('REVIEW NOTE / 否决意见'),
        p('委员会认为该协议“无法计分”。没有正确答案，就没有可辩护的失败。'),
        p('医疗顾问反对删除：受试者的指向具有统计学稳定性。项目主任否决了医疗顾问的反对。'),
        p('M-06 在离开设施前完成了七次复演。我们没有保留颜色，只保留了他没有说出口的那部分。'),
        r('第七次复演的记录：'),
        q('它不在色板上。它不在广播里。它在我嘴里。'),
        stamp('NOT APPROVED / RETAINED', 'red'),
      ]),
    ],
    ['协议 VII', '复演', '未批准'],
  ),
  entry(
    'd08',
    'duty',
    'memo',
    'D-08',
    '夜班清洁清单',
    'C.Y. 22 / NIGHT SHIFT',
    'verified',
    2,
    1,
    [
      frag('d08', 1, '01 / 清单', [
        h('NIGHT CLEANING / SITE 06'),
        p('本清单只适用于 02:00–05:00。不要提前进入走廊。'),
        table(
          ['区域', '操作', '禁止'],
          [
            ['观察室', '擦拭玻璃内侧', '与玻璃上的倒影交谈'],
            ['广播室', '更换磁带，记录颜色', '关闭中文通道'],
            ['水槽间', '排水，静置十分钟', '在水变蓝时使用红色抹布'],
          ],
        ),
        p('墙上的颜色名不要擦掉。先拍照，再记录位置。第二天它们会自己回来。'),
        note('走廊尽头的门今天又开了。不是风。把灯留着，不要过去。'),
        stamp('NIGHT CREW / 06', 'muted'),
      ]),
    ],
    ['清洁', '夜班', '走廊'],
  ),
  entry(
    'i05',
    'intake',
    'intake',
    'I-05',
    '配对异常：B-00',
    'C.Y. 24 / INTAKE',
    'contradicted',
    4,
    3,
    [
      frag('i05', 1, '01 / 配对记录', [
        h('PAIRING RECORD / 异常'),
        p('受试者 A-17 与受试者 B-00 完成配对。'),
        table(
          ['字段', 'A-17', 'B-00'],
          [
            ['档案', '完整', '不存在'],
            ['照片', '有', '空白'],
            ['声纹', '有', '空白'],
            ['首轮响应', '已记录', '已记录'],
          ],
        ),
        p('B-00 在整场校准中只出现过一次：广播念出他的名字时。'),
        p('系统将此次配对评为“高同步”。我们没有找到评价所依据的另一端。'),
        stamp('PAIRING ACCEPTED', 'ink'),
      ]),
      frag('i05', 2, '02 / 手写备注', [
        h('MARGIN / 手写备注'),
        p('不要查 B-00。'),
        p('每一届都有一个 B-00。不是同一个人，是同一个空位。'),
        p('系统不是在配对两名受试者。它在配对两个位置，然后等其中一个位置自己长出人。'),
        r('上一个 B-00 的档案：'),
        q('他们已经把空位留给了我。'),
        stamp('DO NOT CROSS-REFERENCE', 'red'),
      ]),
    ],
    ['B-00', '配对', '空位'],
  ),
  entry(
    'c05',
    'chorus',
    'transcript',
    'C-05',
    '未使用的第七个颜色名',
    'C.Y. 26 / CHORUS',
    'unverified',
    5,
    3,
    [
      frag('c05', 1, '01 / 播报日志', [
        h('CHORUS / UNASSIGNED TOKEN'),
        p('以下片段在自动广播测试中进入中文通道，没有对应的颜色或协议。'),
        table(
          ['时间', '语言', '播报'],
          [
            ['00:02', 'ZH', '缄色。'],
            ['00:06', 'EN', 'Hush.'],
            ['00:11', 'ZH', '请确认你看见的颜色。'],
            ['00:15', 'EN', 'Do not name it.'],
          ],
        ),
        p('测试结束后，受试者开始为该词选择颜色。没有人问它是什么颜色。'),
        note('它不是颜色。它是频道。'),
        stamp('DO NOT ENCODE', 'red'),
      ]),
      frag('c05', 2, '02 / 操作员备注', [
        h('OPERATOR NOTE / 广播员'),
        p('不要把那个词翻译成英文。英文通道会把它读成另一个词，而另一个词有颜色。'),
        p('如果中文通道自己说出它，不要关麦克风。关掉英文通道。等它自己停下来。'),
        p('我们试过删除这段录音。下一次测试时，它从另一盘磁带里回来了。'),
        r('磁带编号：'),
        note('M-06 说，他记住的就是这个词。我们无法确认他说的是哪一个。'),
        stamp('RETAINED / NOT TRANSLATED', 'ink'),
      ]),
    ],
    ['缄色', '第七个颜色', 'M-06'],
  ),
  entry(
    'v06',
    'civilian',
    'ephemera',
    'V-06',
    '工厂色彩纪律培训签到表',
    'C.Y. 23 / PLANT 04',
    'verified',
    3,
    2,
    [
      frag('v06', 1, '01 / 签到表', [
        h('COLOR DISCIPLINE / SHIFT TRAINING'),
        p('签到表印在旧民防海报的背面。海报上的眼睛被裁掉了一半。'),
        table(
          ['班次', '训练编号', '状态'],
          [
            ['白班', 'CH-03', '完成'],
            ['夜班', 'CH-04', '完成'],
            ['维护', 'CH-06', '缺席'],
            ['临时工', 'CH-00', '已签到'],
          ],
        ),
        p('培训内容：警报时只确认指示灯的颜色。不要读警报下方的文字。'),
        p('讲师签名栏被撕去。临时工一栏有九种不同的笔迹，但只有一个名字。'),
        note('工人不需要知道 CHROMA 是什么。他们只需要在警报时选对颜色。'),
        stamp('CIVILIAN STANDARD / 07', 'muted'),
      ]),
    ],
    ['工厂', '培训', '民用扩散'],
  ),
  entry(
    'a04',
    'anomaly',
    'fragment',
    'A-04',
    'M-06 第四残页：离开之后',
    'AFTER SITE 06 / UNSORTED',
    'unverified',
    6,
    4,
    [
      frag('a04', 1, '01 / 录音转写', [
        h('M-06 / FRAGMENT 04'),
        image('/images/lore/horror-face.jpg', '一帧无法确认身份的监控画面：一张仰起的脸，眼睛深陷在黑暗里', 'RECOVERED FRAME / IDENTITY UNCONFIRMED'),
        p('离开设施以后，屏幕没有消失。'),
        p('红绿灯、药盒、别人衣服上的条纹、广告牌背面的反光，全都在要求我确认。'),
        p('我试过不回答。它们不会结束，只会把同一个问题换一种语言再问一遍。'),
        p('他们需要的不是我答对。他们需要的是我还在回答这件事。'),
        note('录音到此中断。背景里有一台终端在播放校准通过的提示音。'),
        stamp('AFTER EXIT / NO LOCATION', 'red'),
      ]),
      frag('a04', 2, '02 / 背面坐标', [
        h('COORDINATES / 背面'),
        p('纸的背面画着一条公交线路。路线经过四个站，站名分别是红、蓝、绿、白。'),
        p('这条线路在市政图上不存在。最后一站也没有名字。'),
        q('如果你找到那个房间，不要坐下。'),
        p('另一个人还在里面回答。'),
        r('地图比例尺：'),
        stamp('MISSING SAMPLE / DO NOT FILE', 'muted'),
      ]),
    ],
    ['M-06', '离开', '地图'],
  ),
  entry(
    'm02',
    'committee',
    'minutes',
    'M-02',
    '复验委员会纪要：标准培训授权',
    'C.Y. 29 / CLOSED',
    'partial',
    6,
    4,
    [
      frag('m02', 1, '01 / 议程', [
        h('DIRECTORATE MINUTES / 标准培训授权'),
        p('会议主题：将 CHROMA 的颜色纪律模块纳入学校、工厂与安保培训。'),
        list([
          '1. 复核民用培训的复验数据。',
          '2. 评估受试者知情同意的表述。',
          '3. 再次讨论关闭 Site 06。',
        ], true),
        table(
          ['议题', '结论', '备注'],
          [
            ['民用培训', '通过', '使用“注意力安全”名称'],
            ['知情同意', '改写', '把同意写进流程，而不是表格'],
            ['关闭设施', '否决', '保留为校准原址'],
          ],
        ),
        note('不要使用“项目”一词。使用“标准”。'),
      ]),
      frag('m02', 2, '02 / 结论', [
        h('RESOLUTION / 结论'),
        p('CHROMA 不再需要 Site 06 作为唯一节点。'),
        p('Site 06 保留为校准原址；协议 IV–VI 不进入民用教材。'),
        p('M-06 不再作为回收目标。其残页转入教学案例，用于说明“个体残留不能代表方法失效”。'),
        p('下一轮复验由外部机构执行。委员会保留最终解释权。'),
        q('The subject is the method.'),
        stamp('STANDARD / APPROVED', 'ink'),
      ]),
    ],
    ['委员会', '标准培训', '系统延续'],
  ),
];

export const LORE_DOC_COUNT = LORE_DOCS.length;
export const LORE_FRAGMENT_COUNT = LORE_DOCS.reduce(
  (total, doc) => total + doc.fragments.length,
  0,
);

export const LORE_BY_ID = new Map(LORE_DOCS.map((doc) => [doc.id, doc]));
export const LORE_FRAGMENT_TO_DOC = new Map(
  LORE_DOCS.flatMap((doc) => doc.fragments.map((fragment) => [fragment.id, doc] as const)),
);
export const LORE_FRAGMENT_BY_ID = new Map(
  LORE_DOCS.flatMap((doc) => doc.fragments.map((fragment) => [fragment.id, fragment] as const)),
);
